import type { Appearance } from "@stripe/stripe-js"
import type { Theme } from "ipctg-inflow-types/InFlowThemes"

/**
 * Stripe Theme Configuration
 *
 * https://stripe.com/docs/elements/appearance-api
 */
const FONT_FAMILY = "Inter, Avenir, Helvetica, Arial, sans-serif"
const FONT_SRC =
  "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap"

/**
 * Gets computed CSS variable value from the document root
 * CSS variables don't work in Stripe iframes, so we need to resolve them
 * Also evaluates calc() expressions since Stripe doesn't support the * operator
 */
function getCSSVariable(varName: string, fallback: string): string {
  if (typeof window === "undefined") return fallback
  const rootStyles = getComputedStyle(document.documentElement)
  const value = rootStyles.getPropertyValue(varName).trim()

  if (!value) return fallback

  // If the value contains calc() with *, we need to evaluate it
  // Create a temporary element to compute the actual value
  if (value.includes("calc") && value.includes("*")) {
    const temp = document.createElement("div")
    temp.style.position = "absolute"
    temp.style.visibility = "hidden"
    temp.style.width = value
    document.body.appendChild(temp)
    const computed = getComputedStyle(temp).width
    document.body.removeChild(temp)

    return computed
  }

  return value
}

/**
 * Creates a Stripe Appearance object
 * Resolves CSS variables for spacing/typography with fallbacks
 *
 * @param theme - Optional SambaTheme. If provided, uses theme colors. If not, uses default colors with CSS variable resolution.
 */
export function createStripeAppearance(theme?: Theme): Appearance {
  // Resolve CSS variables with hard-coded fallbacks
  const fontSize4 = getCSSVariable("--font-size-4", "1rem")
  const fontSize2 = getCSSVariable("--font-size-2", "0.875rem")
  const space1 = getCSSVariable("--space-1", "0.25rem")
  const space3 = getCSSVariable("--space-3", "0.75rem")
  const space4 = getCSSVariable("--space-4", "1rem")
  const radius3 = getCSSVariable("--radius-3", "0.375rem")

  // Use theme colors if provided, otherwise resolve from CSS variables
  const colors = theme
    ? {
        primary: theme.hues.primary,
        neutral: theme.hues.neutral,
        danger: theme.hues.danger,
      }
    : {
        primary: {
          1: getCSSVariable("--primary-1", "#fdfdfe"),
          2: getCSSVariable("--primary-2", "#f8f9fe"),
          3: getCSSVariable("--primary-3", "#eff1fc"),
          4: getCSSVariable("--primary-4", "#e4e8fc"),
          5: getCSSVariable("--primary-5", "#d8ddfb"),
          6: getCSSVariable("--primary-6", "#c8d0f8"),
          7: getCSSVariable("--primary-7", "#b4bded"),
          8: getCSSVariable("--primary-8", "#99a3e1"),
          9: getCSSVariable("--primary-9", "#96a0de"),
          10: getCSSVariable("--primary-10", "#8b95d2"),
          11: getCSSVariable("--primary-11", "#59619a"),
          12: getCSSVariable("--primary-12", "#282d54"),
        },
        neutral: {
          1: getCSSVariable("--neutral-1", "#fdfcfd"),
          2: getCSSVariable("--neutral-2", "#faf9fb"),
          3: getCSSVariable("--neutral-3", "#f2eff3"),
          4: getCSSVariable("--neutral-4", "#eae7ec"),
          5: getCSSVariable("--neutral-5", "#e3dfe6"),
          6: getCSSVariable("--neutral-6", "#dbd8e0"),
          7: getCSSVariable("--neutral-7", "#d0cdd7"),
          8: getCSSVariable("--neutral-8", "#bcbac7"),
          9: getCSSVariable("--neutral-9", "#8e8c99"),
          10: getCSSVariable("--neutral-10", "#84828e"),
          11: getCSSVariable("--neutral-11", "#65636d"),
          12: getCSSVariable("--neutral-12", "#211f26"),
        },
        danger: {
          1: getCSSVariable("--danger-1", "#fffcfc"),
          2: getCSSVariable("--danger-2", "#fff8f8"),
          3: getCSSVariable("--danger-3", "#ffefef"),
          4: getCSSVariable("--danger-4", "#ffe5e5"),
          5: getCSSVariable("--danger-5", "#fdd8d8"),
          6: getCSSVariable("--danger-6", "#f9c6c6"),
          7: getCSSVariable("--danger-7", "#f3aeaf"),
          8: getCSSVariable("--danger-8", "#eb9091"),
          9: getCSSVariable("--danger-9", "#e5484d"),
          10: getCSSVariable("--danger-10", "#dc3d43"),
          11: getCSSVariable("--danger-11", "#cd2b31"),
          12: getCSSVariable("--danger-12", "#381316"),
        },
      }

  return {
    theme: "stripe",

    variables: {
      // Typography
      fontFamily: FONT_FAMILY,
      fontSizeBase: fontSize4,
      fontWeightNormal: "400",
      fontWeightMedium: "500",

      // Colors
      colorPrimary: colors.primary[9],
      colorBackground: colors.neutral[2],
      colorText: colors.primary[12],
      colorTextSecondary: colors.neutral[11],
      colorTextPlaceholder: colors.neutral[9],
      colorDanger: colors.danger[9],

      // Spacing & layout
      spacingUnit: space1,
      gridRowSpacing: space4,
      gridColumnSpacing: space4,

      // Border
      borderRadius: radius3,

      // Checkbox
      colorIconCheckmark: colors.primary[12],
    },

    rules: {
      // Input fields
      ".Input": {
        backgroundColor: colors.neutral[2],
        border: `1px solid ${colors.neutral[6]}`,
        borderRadius: radius3,
        boxShadow: "none",
        color: colors.primary[12],
        padding: `0 ${space3}`,
        lineHeight: "3.25rem",
      },

      ".Input:focus": {
        backgroundColor: colors.neutral[3],
        borderColor: colors.neutral[8],
        boxShadow: "none",
      },

      ".Input--invalid": {
        backgroundColor: colors.danger[2],
        borderColor: colors.danger[8],
      },

      ".Input::placeholder": {
        color: colors.neutral[9],
      },

      // Labels
      ".Label": {
        color: colors.primary[12],
        fontSize: fontSize2,
        fontWeight: "500",
      },

      // Error messages
      ".Error": {
        color: colors.danger[10],
        fontSize: fontSize2,
      },

      // Payment method tabs
      ".Tab": {
        backgroundColor: colors.neutral[3],
        border: `1px solid ${colors.neutral[6]}`,
      },

      ".Tab--selected": {
        backgroundColor: colors.primary[3],
        border: `2px solid ${colors.primary[9]}`,
      },

      ".TabLabel": {
        color: colors.neutral[12],
        fontWeight: "500",
      },

      ".TabIcon": {
        fill: colors.neutral[12],
      },

      // Checkbox styling - keep background neutral, just show checkmark
      ".CheckboxInput": {
        backgroundColor: colors.neutral[2],
        border: `1px solid ${colors.neutral[6]}`,
        borderRadius: radius3,
        boxShadow: "none",
      },

      ".CheckboxInput--checked": {
        backgroundColor: colors.neutral[2],
        borderColor: colors.primary[9],
      },
    },
  }
}

/**
 * Stripe fonts configuration
 */
export const stripeFonts = [
  {
    cssSrc: FONT_SRC,
  },
]
